import type { LotStoryProjectionIdentityContext } from '../../types/lotStory'

type ActiveDescendant = LotStoryProjectionIdentityContext['activeDescendants'][number]

export type StoryActiveDescendantLocationGroup = {
  locationKey: string
  locationLabel: string
  lotCount: number
  lotKeys: string[]
}

type BuildStoryActiveDescendantLocationGroupsParams = {
  descendants: ActiveDescendant[]
  resolveLocationLabel: (descendant: ActiveDescendant) => string | undefined
  fallbackLocationLabel: string
}

const normalizeToken = (value: string | undefined): string | undefined => {
  const normalized = value?.trim()
  return normalized ? normalized : undefined
}

const resolveGroupLocationKey = (descendant: ActiveDescendant): string => {
  const locationType = descendant.locationType ?? 'unknown'
  const farmId = normalizeToken(descendant.farmId)
  const productionUnitId = normalizeToken(descendant.productionUnitId)
  const locationId = normalizeToken(descendant.locationId)

  if (locationType === 'farm') {
    return `farm:${farmId ?? locationId ?? 'unknown'}`
  }

  if (locationType === 'production-unit') {
    return `production-unit:${farmId ?? 'unknown'}:${productionUnitId ?? locationId ?? 'unknown'}`
  }

  if (locationType === 'processing-site') {
    return `processing-site:${farmId ?? 'unknown'}:${locationId ?? productionUnitId ?? 'unknown'}`
  }

  return `unknown:${locationId ?? farmId ?? productionUnitId ?? 'unknown'}`
}

export const buildStoryActiveDescendantLocationGroups = ({
  descendants,
  resolveLocationLabel,
  fallbackLocationLabel
}: BuildStoryActiveDescendantLocationGroupsParams): StoryActiveDescendantLocationGroup[] => {
  const groups = new Map<string, { label: string; lotKeysById: Map<string, string> }>()

  descendants.forEach((descendant) => {
    const lotId = normalizeToken(descendant.lotId)
    if (!lotId) return

    const locationKey = resolveGroupLocationKey(descendant)
    const locationLabel = normalizeToken(resolveLocationLabel(descendant)) ?? fallbackLocationLabel
    const lotKey = normalizeToken(descendant.lotKey) ?? lotId

    const group = groups.get(locationKey)
    if (!group) {
      groups.set(locationKey, {
        label: locationLabel,
        lotKeysById: new Map([[lotId, lotKey]])
      })
      return
    }

    if (!group.lotKeysById.has(lotId)) {
      group.lotKeysById.set(lotId, lotKey)
    }
  })

  return Array.from(groups.entries())
    .map(([locationKey, group]) => {
      const lotKeys = Array.from(group.lotKeysById.values()).sort((left, right) => left.localeCompare(right))
      return {
        locationKey,
        locationLabel: group.label,
        lotCount: lotKeys.length,
        lotKeys
      }
    })
    .sort((left, right) => {
      if (right.lotCount !== left.lotCount) {
        return right.lotCount - left.lotCount
      }
      return left.locationLabel.localeCompare(right.locationLabel)
    })
}